import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Component/CSS/SeriesPlayScreen.module.css';
import Loading from '../../common/HousGlass Loader/Loading';
import { useAudioPlayer } from '../../components/audio/AudioPlayerContext';
import { useAddContinueListening } from '../../hooks/useContinueListening';
import useAuthStore from '../../store/authStore';

const AudiosPlayScreen = ({ audioData, chapters, series }) => {

  // Use the global audio player context
  const { currentAudio, playAudio, isPlaying } = useAudioPlayer();
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const { addContinueListening } = useAddContinueListening();
  
  // Handle case when data is not yet loaded
  if (!audioData && !series) {
    return <div className={styles.loading}><Loading/></div>;
  }
  
  // Determine if we should use series data
  const useSeriesData = !!series;
  
   // Get the appropriate data source
  const displayData = {
    title: useSeriesData ? series.title : audioData.title,
    description: useSeriesData ? series.description : audioData.description,
    genre_name: useSeriesData ? audioData.genre_name : audioData.genre_name,
    language: useSeriesData ? series.language : audioData.language,
    cover_image: useSeriesData && series?.audio_type!=="Audio Series" 
      ? series.cover_image_url 
      : (audioData.computer_image_url || audioData.mobile_image_url),
    author_name: useSeriesData 
      ? (series.created_by ? "Created by " + series.created_by : "Unknown Author")
      : (audioData.author_name || "Unknown Author"),
    id: useSeriesData ? audioData.id :audioData.id,
    slug: useSeriesData ? series.slug : audioData.slug,
    duration: audioData ? audioData.duration : null
  };
  
  // Find the first audio chapter if chapters exist
  const firstAudioChapter = chapters && chapters.length > 0 
    ? chapters.find(chapter => chapter.type === "audio") 
    : null;

  // Updated handleListenClick function
  const handleListenClick = () => {
    // Add continue listening record if authenticated
    if (isAuthenticated) {
      addContinueListening({
        item_type: "audio",
        item_id: displayData.id,
      });
    }
    
    if (firstAudioChapter) {
      // Format chapter data for the audio player
      const audioPlayerData = {
        id: firstAudioChapter.id,
        title: firstAudioChapter.audio_title || displayData.title,
        author: displayData.author_name,
        song_url: firstAudioChapter.audio_url,
        image: firstAudioChapter.audio_computer_image || 
               firstAudioChapter.audio_mobile_image || 
               displayData.cover_image,
        duration: firstAudioChapter.audio_duration,
        audioId: firstAudioChapter.audio_id,
        slug: displayData.slug || firstAudioChapter.slug,
      };
      
      playAudio(audioPlayerData);
      
    } else if (audioData && audioData.audio_url) {
      // If no chapters but the audio itself has a URL
      const audioPlayerData = {
        id: audioData.id,
        title: displayData.title,
        author: displayData.author_name,
        song_url: audioData.audio_url,
        image: displayData.cover_image,
        duration: audioData.duration,
        audioId: audioData.id,
        slug: displayData.slug,
      };
      
      playAudio(audioPlayerData);
    }
  };
  
  // Handle language click to navigate to AudiosByLanguage page
  const handleLanguageClick = () => {
    if (displayData.language) {
      navigate(`/collection/language/${displayData.language.toLowerCase()}`);
    }
  };

  const handleEpisodeClick = () => { 
    if (displayData.genre_name) {
      navigate(`/collection/category/${displayData.genre_name.toLowerCase()}`);
    }
  };
  
  // Check if the current audio is playing
  const isCurrentAudioPlaying = () => {
    if (firstAudioChapter) {
      return currentAudio && currentAudio.id === firstAudioChapter.id;
    }
    return currentAudio && currentAudio.id === displayData.id;
  };
  
  // Format duration from seconds to minutes:seconds
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Determine if we should show the episodes count
  const hasChapters = chapters && chapters.length > 0;
  const episodesCount = useSeriesData ? series.total_chapters : (chapters ? chapters.length : 0);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.imageSection}>
          <img
            src={displayData.cover_image}
            alt="Audio Cover"
            className={styles.coverImage}
          />
        </div>
        
        <div className={styles.contentSection}>
          <h1 className={styles.title}>{displayData.title}</h1>
          
          <div className={styles.metadataSection}>
            <div className={styles.metadataItem} 
              onClick={handleEpisodeClick}
              style={{ cursor: 'pointer' }}
            >
              <span className={styles.metadataLabel}>Genre:</span>
              <span className={`${styles.metadataValue} ${styles.metadataValueLit}`}>{audioData.genre_name}</span>
 
            </div>
            <div 
              className={`${styles.metadataItem} ${styles.clickableMetadata}`}
              onClick={handleLanguageClick}
              style={{ cursor: 'pointer' }}
            >
              <span className={styles.metadataLabel}>Language:</span>
              <span className={`${styles.metadataValue} ${styles.metadataValueLit}`}>{displayData.language}</span>
            </div>
            {(hasChapters || useSeriesData) && (
              <div className={styles.metadataItem} >
                <span className={styles.metadataLabel}>Episodes:</span>
                <span className={styles.metadataValue}>{episodesCount}</span>
              </div>
            )}
            {displayData.duration && (
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>Duration:</span>
                <span className={styles.metadataValue}>{formatDuration(displayData.duration)}</span>
              </div>
            )}
          </div>
          
          <p className={styles.description}>{displayData.description}</p>
          
          <div className={styles.buttonContainer}>
            <button
              className={styles.redButton}
              onClick={handleListenClick}
              disabled={!firstAudioChapter?.audio_url && (!audioData || !audioData.audio_url)}
            >
              {isCurrentAudioPlaying() && isPlaying ? "Listen Now" : "Listen Now"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudiosPlayScreen;